import { Injectable } from 'angular-ts-decorators';

import {
  IconDefinition as duotoneIconDefinition,
  faAbacus,
  faBarcodeRead,
  faBlanket,
  faChartLine,
  faForklift,
  faForward,
  faHandshakeAlt,
  faInventory,
  faPenAlt,
  faPlanetRinged,
  faTags,
  faTools,
  faUsers,
  faUserPen,
} from '@fortawesome/pro-duotone-svg-icons';
import { flow, extend, find, forEach, intersectionBy, filter } from 'lodash';

import { CurrentSession } from '@shared/app/scripts/services/current-session';
import { Domain } from '@shared/app/scripts/services/domain';
import type { IConfig } from '@shared/app/scripts/services/config';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';

/**
 * Make sure the supported applications get created using a defined type
 */
interface ISupportedApplication {
  url: string;
  icon: duotoneIconDefinition;
  application: string;
  hasAccess?: boolean;
  target?: '_blank';
}

function replaceSubdomain($window, newSubdomain) {
  let currentHostname = $window.location.hostname;
  let currentProtocol = $window.location.protocol;
  let parts = currentHostname.split('.');

  if (parts.length >= 2 && parts[0] === 'www') {
    parts[0] = newSubdomain;
  } else {
    parts.unshift(newSubdomain);
  }

  return currentProtocol + '//' + parts.join('.');
}

/**
 * Factory in the coreModule. This service loads the available Softwear
 * applications.
 */
@Injectable('applications')
export class Applications {
  private url: string;
  private supportedApplications: ISupportedApplication[];

  constructor(
    private $http: ng.IHttpService,
    private $q: ng.IQService,
    private $window: ng.IWindowService,
    private currentSession: CurrentSession,
    private standardErrorParser: IStandardErrorParser,
    config: IConfig,
    domain: Domain,
  ) {
    'ngInject';

    this.url = '//' + domain.sibling(config.spynlSubdomain);

    this.supportedApplications = [
      {
        url: `${replaceSubdomain($window, 'pos')}`,
        icon: faTags,
        application: 'pos',
      },
      {
        url: 'play',
        icon: faForward,
        application: 'secondscreen',
      },
      {
        url: 'dashboard#',
        icon: faChartLine,
        application: 'dashboard',
      },
      {
        url: `${replaceSubdomain($window, 'pos')}/logistic.html`,
        icon: faAbacus,
        application: 'inventory',
      },
      {
        url: 'sales#',
        icon: faHandshakeAlt,
        application: 'sales',
      },
      {
        url: 'logistics#',
        icon: faInventory,
        application: 'logistics',
      },
      {
        url: 'picking#',
        icon: faForklift,
        application: 'picking',
      },
      {
        url: 'admin#',
        icon: faUsers,
        application: 'admin',
      },
      {
        url: `${replaceSubdomain($window, 'softwear6')}/logistic.html`,
        icon: faBlanket,
        application: 'polytex',
      },
      {
        url: 'https://my.shopsettings.com/p/softwear',
        icon: faPlanetRinged,
        application: 'ecwid_link',
        target: '_blank',
      },
      {
        url: 'https://backoffice.softwearasp.nl/',
        icon: faPenAlt,
        application: 'foxpro_backoffice',
        target: '_blank',
      },
      {
        url: `https://www.latestcollection.fashion/products?sid=${this.currentSession.sid}`,
        icon: faBarcodeRead,
        application: 'products',
        target: '_blank',
      },
      {
        url: 'https://hardwearshop.nl',
        icon: faTools,
        application: 'hardwearshop',
        target: '_blank',
      },
      {
        url: 'customer#',
        icon: faUserPen,
        application: 'customer',
      },
    ];
  }

  /**
   *
   * Returns a object that contains all of the available applications and
   * options. This object contains metadata for description, options, locale id
   * @returns {httpPromise} resolve with a object containing
   * applications
   */
  all(): ng.IPromise<any> {
    return this.$http
      .get(this.url + '/about/applications')
      .catch(this.standardErrorParser)
      .then((response: any) => response.data.applications)
      .catch((error) => this.$q.reject(error.data.message));
  }

  /**
   *
   * Returns a object that contains all applications for a particular tenant.
   * @param {string} tenantId
   * The tenant id of the account. This is an optional parameter. If not provided,
   * it will only change users on the current account.
   * @returns {httpPromise} resolve with a object containing
   * applications for the current tenant
   */
  byTenant(tenantId?: string): ng.IPromise<any> {
    const tenantPortion = tenantId ? '/tenants/' + tenantId : '';
    return this.$http
      .get(this.url + tenantPortion + '/tenants/get-applications')
      .catch(this.standardErrorParser)
      .then((response: any) => response.data.data)
      .catch((error) => this.$q.reject(error.data.message));
  }

  /**
   *
   * Returns an array of all supported applications. Can be used to display
   * applications in a list view.
   * @returns {httpPromise} resolve with a object containing
   * applications
   */
  supported(): ng.IPromise<any> {
    // TODO figure out a beter way for getting this
    return this.all()
      .then((allApps) => {
        const cleanedApps = intersectionBy(
          allApps,
          this.supportedApplications,
          'application',
        );
        this.supportedApplications = cleanedApps.map((app: any) => {
          const object = this.supportedApplications
            .filter((item: any) => item.application === app.application)
            .reduce((memo, item) => item, {});
          return extend(app, object);
        });
      })
      .then(() => {
        return this.currentSession.applications
          ? this.currentSession.applications
          : {};
      })
      .then((userApps) => {
        forEach(this.supportedApplications, (app) => {
          app.hasAccess = !!userApps[app.application];
        });

        return this.supportedApplications;
      });
  }

  /**
   *
   * Returns an array of all supported applications by tenantId
   * @param {string} tenantId the id of the tenant account
   * @returns {httpPromise} resolve with a object containing
   * applications
   */
  supportedByTenant(tenantId): ng.IPromise<any> {
    return this.all()
      .then((allApps) => {
        const cleanedApps: any = flow([
          (x) => intersectionBy(x, this.supportedApplications, 'application'),
          (x) => filter(x, (item: any) => item.internal === undefined),
        ])(allApps);

        this.supportedApplications = cleanedApps.map(
          (app: ISupportedApplication) => {
            const object = this.supportedApplications
              .filter((item) => item.application === app.application)
              .reduce((memo, item) => item, {});
            return extend(app, object);
          },
        );
      })
      .then(() => this.byTenant(tenantId))
      .then((tenantApps) => {
        forEach(this.supportedApplications, (app: ISupportedApplication) => {
          const tenantApp: any = find(tenantApps, {
            id: app.application,
          });

          if (tenantApp) {
            app.hasAccess = tenantApp.has_access;
          } else {
            app.hasAccess = false;
          }
        });
        return this.supportedApplications;
      });
  }

  /**
   *
   * Change the access to an application for the requested tenant. The status
   * is either toggled, or set to the value of the has_access parameter if
   * provided. Only users with write access to the tenant can make this change.
   * At the moment, only Softwear employees have this right. An email will be
   * sent to all current tenant owners if a change was made.
   * @param {string} application the application key
   * @param {boolean} access true gives access, false takes away access
   * @param {string} tenantId
   * The tenant id of the account. This is an optional parameter. If not provided,
   * it will only change users on the current account.
   * @returns {httpPromise} resolve with a object containing
   * applications for the current tenant
   */
  changeAccess(
    application: string,
    access: boolean,
    tenantId?: string,
  ): ng.IPromise<any> {
    const tenantPortion = tenantId ? '/tenants/' + tenantId : '';
    return this.$http
      .post(this.url + tenantPortion + '/tenants/change-application-access', {
        application: application,
        has_access: access,
      })
      .catch(this.standardErrorParser)
      .then((response: any) => response.data.message)
      .catch((error) => this.$q.reject(error.data.message));
  }
}
